import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getApiUrl } from '../config/api';
import { Eye, EyeOff, Mail, Lock, User, Building2, ArrowRight, CheckCircle, Wrench, Car, Sparkles, ChevronDown, Paintbrush, Settings, Star, Square } from 'lucide-react';

// Betriebsarten mit zugehörigen Modulen
const BUSINESS_TYPES = [
  {
    id: 'autoglas',
    label: 'Autoglaswerkstatt',
    icon: Square,
    iconColor: 'text-blue-500',
    bgColor: 'bg-blue-50',
    description: 'Spezialisiert auf Glasreparatur und -austausch',
    modules: ['dat_glass']
  },
  {
    id: 'karosserie',
    label: 'Karosserie- und Lackierbetrieb',
    icon: Paintbrush,
    iconColor: 'text-orange-500',
    bgColor: 'bg-orange-50',
    description: 'Karosserie-, Unfall- und Lackierarbeiten',
    modules: ['dat_myclaim']
  },
  {
    id: 'reparatur',
    label: 'Reparaturwerkstatt',
    icon: Wrench,
    iconColor: 'text-gray-600',
    bgColor: 'bg-gray-50',
    description: 'Allgemeine Fahrzeuginstandsetzung',
    modules: ['dat_myclaim']
  },
  {
    id: 'kombi',
    label: 'Autoglas + Karosserie/Lack',
    icon: Star,
    iconColor: 'text-purple-500',
    bgColor: 'bg-purple-50',
    description: 'Kombibetrieb mit allen Leistungen',
    modules: ['dat_glass', 'dat_myclaim']
  }
];

const Login = ({ setUser }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [businessDropdownOpen, setBusinessDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isRegister && !acceptTerms) {
      toast.error('Bitte akzeptieren Sie die AGB und Datenschutzbestimmungen');
      return;
    }
    
    if (isRegister && password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    
    if (isRegister && !businessType) {
      toast.error('Bitte wählen Sie Ihre Betriebsart aus');
      return;
    }
    
    setLoading(true);

    try {
      const endpoint = isRegister ? '/api/auth/register' : '/api/auth/login';
      
      // Bei Registrierung: Betriebsart und Module mitschicken
      const selectedBusinessType = BUSINESS_TYPES.find(bt => bt.id === businessType);
      const body = isRegister 
        ? { 
            email: email.toLowerCase(), 
            password, 
            name: companyName || name, 
            role: 'admin',
            business_type: businessType,
            modules: selectedBusinessType?.modules || []
          }
        : { email: email.toLowerCase(), password };

      const response = await fetch(getApiUrl() + endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.access_token);
        setUser(data.user);
        toast.success(isRegister ? '🎉 Willkommen bei Easyclaim24!' : '✅ Erfolgreich angemeldet!');
        navigate('/');
      } else {
        toast.error(data.detail || 'Anmeldung fehlgeschlagen');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Verbindungsfehler - bitte versuchen Sie es später erneut');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{backgroundColor: '#F5F7FA'}}>
      {/* Left Side - Branding (schmaler) */}
      <div className="hidden lg:flex lg:w-2/5 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 p-10 flex-col justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-4">
            Professionelles AutoschadenManagement
          </h1>
          <p className="text-lg text-blue-100 mb-8">
            Verwalten Sie Ihre Glasschäden effizient und professionell.
          </p>
          
          {/* Features */}
          <div className="space-y-3">
            {[
              'DAT myClaim Integration',
              'Automatische Rechnungserstellung',
              'Digitale Abtretungserklärung',
              'KI-gestützter PDF-Scanner',
              'Multi-Mandanten-fähig'
            ].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3 text-blue-100">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="text-blue-200 text-sm">
          © 2024 Easyclaim24 - Alle Rechte vorbehalten
        </div>
      </div>
      
      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo über dem Formular - immer sichtbar */}
          <div className="text-center mb-8">
            <img 
              src="/easyclaim24-clean.png" 
              alt="Easyclaim24 Logo" 
              className="h-24 w-auto mx-auto"
            />
            <p className="text-gray-500 text-sm mt-3 lg:hidden">Professionelles AutoschadenManagement</p>
          </div>
          
          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {isRegister ? 'Konto erstellen' : 'Willkommen zurück'}
              </h2>
              <p className="text-gray-600 mt-1">
                {isRegister 
                  ? 'Starten Sie jetzt mit Easyclaim24' 
                  : 'Melden Sie sich in Ihrem Konto an'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {isRegister && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Firmenname *
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        placeholder="z.B. AutoGlas Müller GmbH"
                        required={isRegister}
                        data-testid="company-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ihr Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        placeholder="Max Mustermann"
                        required={isRegister}
                        data-testid="name-input"
                      />
                    </div>
                  </div>
                  
                  {/* Betriebsart-Auswahl als Custom Dropdown mit Icons */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Betriebsart *
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setBusinessDropdownOpen(!businessDropdownOpen)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white text-left flex items-center justify-between"
                        data-testid="business-type-select"
                      >
                        {businessType ? (
                          <div className="flex items-center gap-3">
                            {(() => {
                              const selected = BUSINESS_TYPES.find(bt => bt.id === businessType);
                              const IconComponent = selected?.icon;
                              return (
                                <>
                                  <div className={`w-8 h-8 rounded-lg ${selected?.bgColor} flex items-center justify-center`}>
                                    {IconComponent && <IconComponent className={`w-4 h-4 ${selected?.iconColor}`} />}
                                  </div>
                                  <span className="text-gray-900">{selected?.label}</span>
                                </>
                              );
                            })()}
                          </div>
                        ) : (
                          <span className="text-gray-400">Bitte wählen...</span>
                        )}
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${businessDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {/* Dropdown Menu */}
                      {businessDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                          {BUSINESS_TYPES.map((type) => {
                            const IconComponent = type.icon;
                            return (
                              <button
                                key={type.id}
                                type="button"
                                onClick={() => {
                                  setBusinessType(type.id);
                                  setBusinessDropdownOpen(false);
                                }}
                                className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                                  businessType === type.id ? 'bg-blue-50' : ''
                                }`}
                              >
                                <div className={`w-10 h-10 rounded-lg ${type.bgColor} flex items-center justify-center`}>
                                  <IconComponent className={`w-5 h-5 ${type.iconColor}`} />
                                </div>
                                <div className="flex-1 text-left">
                                  <div className="font-medium text-gray-900">{type.label}</div>
                                  <div className="text-xs text-gray-500">{type.description}</div>
                                </div>
                                {businessType === type.id && (
                                  <CheckCircle className="w-5 h-5 text-blue-500" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    
                    {businessType && (
                      <div className="mt-2 p-2 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-xs text-green-700">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          <strong>Freigeschaltete Module:</strong>{' '}
                          {BUSINESS_TYPES.find(bt => bt.id === businessType)?.modules.map(m => 
                            m === 'dat_glass' ? 'DAT Glaskalkulation' : 'DAT MyClaim'
                          ).join(' + ')}
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-Mail-Adresse *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="ihre@email.de"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Passwort *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder={isRegister ? 'Mindestens 6 Zeichen' : '••••••••'}
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {isRegister && (
                  <p className="text-xs text-gray-500 mt-1">Mindestens 6 Zeichen</p>
                )}
              </div>

              {isRegister && (
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="terms" className="text-sm text-gray-600">
                    Ich akzeptiere die{' '}
                    <a href="/impressum" className="text-blue-600 hover:underline">AGB</a>
                    {' '}und{' '}
                    <a href="/datenschutz" className="text-blue-600 hover:underline">Datenschutzbestimmungen</a>
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-button"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Bitte warten...
                  </>
                ) : (
                  <>
                    {isRegister ? 'Kostenlos registrieren' : 'Anmelden'}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setIsRegister(!isRegister);
                  setAcceptTerms(false);
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                data-testid="toggle-mode-button"
              >
                {isRegister 
                  ? '← Bereits registriert? Anmelden' 
                  : 'Noch kein Konto? Jetzt registrieren →'}
              </button>
            </div>
          </div>
          
          {/* Footer Links */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <a href="/impressum" className="hover:text-gray-700">Impressum</a>
            <span className="mx-2">•</span>
            <a href="/datenschutz" className="hover:text-gray-700">Datenschutz</a>
            <span className="mx-2">•</span>
            <a href="/barrierefreiheit" className="hover:text-gray-700">Barrierefreiheit</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
